import {Clone, E, StartDownload} from "js-vextensions";
import React, {useState} from "react";
import {Button, Column, DropDown, DropDownContent, DropDownTrigger, Row, Text, TextArea, TextInput} from "react-vcomponents";
import {AddGlobalStyle, BaseComponent} from "react-vextensions";
import {ShowMessageBox} from "react-vmessagebox";
import {Observer, RunInAction} from "web-vcore";
import {StatView, StatView_NormalKeys} from "../../Server/DB/statViews/@StatView.js";
import {AddStatView, DeleteStatView, UpdateStatView} from "../../Server/Commands/StatViewCommands.js";
import {MeID} from "../../Server/DB/users.js";
import {GetStatViews} from "../../Server/DB/statViews.js";
import {SortKeysInObjectTree} from "../../Utils/General/General.js";
import {zIndexes} from "../../Utils/UI/ZIndexes.js";
import {store} from "../../Store/index.js";
import {observer_mgl} from "mobx-graphlink";
import {ScrollView} from "react-vscrollview";
import moment from "moment";

export function StatsPage_GetTopOffsetForDropdown() {
	// if screen-width <800px, setting-button probably got pushed to 2nd/3rd row (assume 3rd)
	return window.innerWidth < 800 ? 80 : 32;
}

export const JourneyStatsViewsDropdown = observer_mgl(()=>{
	const uiState = store.main.stats;
	const {view: currentView} = uiState;
	const meID = MeID();
	if (meID == null) return null;
	const views = GetStatViews(meID).OrderBy(a=>a.createdAt).OrderBy(a=>a.name);

	return (
		<DropDown style={{marginLeft: "auto", marginRight: 5, position: "relative"}}>
			<DropDownTrigger>
				<Button mdIcon="folder-cog" size={32}/>
			</DropDownTrigger>
			<DropDownContent style={{
				zIndex: zIndexes.dropDown, position: "fixed", right: 0,
				top: StatsPage_GetTopOffsetForDropdown(),
				bottom: 0, // bottom:0 = stop at bottom of screen
				background: "rgba(0,0,0,.9)", borderRadius: 5,
			}}>
				<ScrollView style={{height: "100%"}}>
					<Row>
						<Text>Views:</Text>
						<Button ml={5} mdIcon="plus" onClick={()=>{
							const view = Clone(currentView);
							view.name = "(new view)";
							new AddStatView({entry: view}).RunOnServer();
						}}/>
						<Button ml="auto" text="Export" onClick={()=>{
							const output = JSON.stringify(views, null, "\t");
							StartDownload(output, `StatViewsExport_${moment().format("YYYY-MM-DD-HH-mm-ss")}.json`);
						}}/>
					</Row>
					{/*<ScrollView style={{flex: 1}}>*/}
					{views.map((view, index)=>{
						return (
							<Row key={view.id} style={{padding: "5px 0"}}>
								<Button text={view.name} style={E(
									{flex: 1},
									view.id == uiState.lastAppliedViewID && {backgroundColor: "rgba(100,200,100,.5)"}
								)} onClick={()=>{
									// for each property in view, apply it to the current view
									RunInAction("ViewsDropdown.ApplyClickedView", ()=>{
										const newView = Clone(currentView);
										for (const [key, storedVal] of Object.entries(view)) {
											newView[key] = storedVal;
										}
										for (const [key, overrideVal] of Object.entries(uiState.viewLocks)) {
											if (overrideVal != null) {
												newView[key] = overrideVal;
											}
										}
										uiState.view = newView;
										uiState.lastAppliedViewID = view.id!;
									});
								}}/>
								<StatViewSettingsDropdown viewInDB={view}/>
							</Row>
						);
					})}
				</ScrollView>
			</DropDownContent>
		</DropDown>
	);
});

AddGlobalStyle(`
@position-try --flip-up {
	top: auto;
	bottom: anchor(top);
}
@position-try --flip-right {
	right: auto;
	left: anchor(right);
}
@position-try --flip-up-right {
	top: auto;
	bottom: anchor(top);
	right: auto;
	left: anchor(right);
}
`);

const StatViewSettingsDropdown = observer_mgl((props: {viewInDB: StatView})=>{
	const {viewInDB} = props;
	const [editing, setEditing] = useState(false);
	const uiState = store.main.stats;
	const {view: currentView} = uiState;
	const meID = MeID();
	if (meID == null) return null;

	const anchorName = `--stat-view-settings-trigger-${viewInDB.id}`;
	return (
		<DropDown style={{marginLeft: 5}}>
			<DropDownTrigger>
				<Button mdIcon="cog" style={{anchorName}}/>
			</DropDownTrigger>
			<DropDownContent style={{
				zIndex: zIndexes.dropDown, background: "rgba(0,0,0,.9)", borderRadius: 5,
				position: "fixed", positionAnchor: anchorName, top: "anchor(bottom)", right: "anchor(right)",
				positionTryFallbacks: "--flip-up, --flip-right, --flip-up-right, flip-block, flip-inline, flip-block flip-inline",
			}}>
				<Column style={{padding: 10}}>
					<Row>
						<Text>Name: </Text>
						<TextInput style={{width: 200}} value={viewInDB.name} onChange={val=>{
							new UpdateStatView({id: viewInDB.id!, updates: {name: val}}).RunOnServer();
						}}/>
					</Row>
					<Button mt={5} text="Overwrite with current (all fields)" style={{fontSize: 12}} onClick={()=>{
						ShowMessageBox({
							title: "Overwrite stored view with current view (all fields)", cancelButton: true,
							message: `Overwrite stored view "${viewInDB.name}" with current view? (all fields)`,
							onOK: ()=>{
								const viewUpdates = GetPropertySubsetOfView(currentView, StatView_NormalKeys(), true, viewInDB);
								new UpdateStatView({id: viewInDB.id!, updates: viewUpdates}).RunOnServer();
							},
						});
					}}/>
					<Button mt={5} text="Overwrite with current (existing fields)" style={{fontSize: 12}} onClick={()=>{
						ShowMessageBox({
							title: "Overwrite stored view with current view (existing fields)", cancelButton: true,
							message: `Overwrite stored view "${viewInDB.name}" with current view? (existing fields)`,
							onOK: ()=>{
								const viewUpdates = GetPropertySubsetOfView(currentView, Object.keys(viewInDB), true, viewInDB);
								new UpdateStatView({id: viewInDB.id!, updates: viewUpdates}).RunOnServer();
							},
						});
					}}/>
					<Button mt={5} text="Edit JSON" style={{fontSize: 12}} onClick={()=>{
						//let newJSON = ToJSON_WithSpaces(config);
						const configCopy = SortKeysInObjectTree(viewInDB);
						let newJSON = JSON.stringify(configCopy, undefined, 4);
						const boxController = ShowMessageBox({
							title: `Edit json for view "${viewInDB.name}"`, cancelButton: true,
							message: ()=>(
								//<Column style={DialogStyle({width: 800})}>
								<Column>
									<Row>View:</Row>
									<TextArea style={{width: 800..KeepAtMost(window.innerWidth - 100), height: window.innerHeight - 200}} value={newJSON} onChange={val=>{
										newJSON = val;
										boxController.UpdateUI();
									}}/>
								</Column>
							),
							okOnEnterKey: false, // enter key needed for adding new-lines!
							onOK: ()=>{
								const newJSONObj = JSON.parse(newJSON);
								new UpdateStatView({id: viewInDB.id!, newData: newJSONObj}).RunOnServer();
							},
						});
					}}/>
					<Button mt={5} text="Delete view" style={{fontSize: 12}} onClick={()=>{
						ShowMessageBox({
							title: "Delete view", cancelButton: true,
							message: `Delete view "${viewInDB.name}"?`,
							onOK: ()=>{
								new DeleteStatView({id: viewInDB.id!}).RunOnServer();
							},
						});
					}}/>
				</Column>
			</DropDownContent>
		</DropDown>
	);
});

export function GetPropertySubsetOfView(view: StatView, propertyNames: string[], excludeLockedProps: boolean, ignorePropsUnchangedFromX?: StatView) {
	if (excludeLockedProps) propertyNames = propertyNames.Exclude("creator", "createdAt");

	const viewSubset = {} as StatView;
	for (const [key, val] of Object.entries(view)) {
		if (!propertyNames.includes(key)) continue;
		if (ignorePropsUnchangedFromX != null && ignorePropsUnchangedFromX[key] == val) continue;
		viewSubset[key] = view[key];
	}
	return viewSubset;
}